import { useRef } from "react";
import { InfoIcon, PaperclipIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { EXTENSIONES_ACEPTADAS, RecuadroPunteado } from "./RecuadroDeSoltar";
import { formatearPeso, MAXIMO_BYTES } from "./ZonaDeImagen";

/** Espejo de la regla `mimes:jpeg,png,webp` del servidor. */
const TIPOS_ACEPTADOS = ["image/jpeg", "image/png", "image/webp"];

/**
 * Lo que el navegador puede rechazar sin gastar la subida, o `null` si pasa.
 *
 * Es la MISMA regla que `motivoDeRechazo` de {@link ZonaDeImagen} y comparte su
 * tope (`MAXIMO_BYTES`), que a su vez espeja
 * `GuardarImagenDelInformeRequest::MAXIMO_KB` — el backend reusa ese request
 * para estas imágenes justamente para que el tope viva en un solo lugar.
 *
 * NO es la autoridad y no pretende serlo: la validación real es la del
 * servidor, que mira los BYTES con finfo y no el tipo que declara el sistema
 * operativo. Esto existe para no mandar 10 MB por la red antes de descubrir que
 * no entran, y su 422 se pinta igual cuando llega.
 */
export function motivoDeRechazoDeLaImagen(archivo: File): string | null {
  if (!TIPOS_ACEPTADOS.includes(archivo.type)) {
    return "La imagen debe ser JPG, PNG o WEBP.";
  }
  if (archivo.size > MAXIMO_BYTES) {
    return "La imagen no puede pesar más de 10 MB.";
  }
  return null;
}

/**
 * Un recuadro para UNA imagen: la captura del límite de un rol o uno de los
 * tres encuadres fijos del predio.
 *
 * PRESENTACIONAL A PROPÓSITO, igual que {@link ZonaDeImagen} y
 * {@link ArchivosDelDocumento}: no sabe de la API. Recibe qué mostrar y avisa
 * qué eligió la persona; quien sube es la pantalla, que tiene el id del informe
 * y los hooks.
 *
 * ES UNA SOLA IMAGEN Y SIEMPRE SE REEMPLAZA, al revés que los archivos de
 * antecedentes, donde de una misma categoría llegan el informe, sus anexos y la
 * planilla. Acá cada recuadro responde una pregunta y esa pregunta tiene una
 * respuesta, así que el botón dice "Reemplazar" en cuanto hay algo.
 *
 * MUESTRA UNA MINIATURA Y NO EL NOMBRE DEL ARCHIVO. El prototipo dibuja la
 * fila con su nombre, pero el sistema no guarda el nombre original de estas
 * imágenes —tampoco lo hace la satelital— y de todos modos la miniatura
 * responde mejor la única pregunta que uno se hace mirando esta pantalla: si la
 * captura que subió es la del predio correcto. Un nombre de archivo no lo dice.
 */
export function TarjetaDeImagen({
  titulo,
  ayuda,
  nota,
  src,
  mime,
  peso,
  ocupado = false,
  error,
  onArchivo,
  onQuitar,
  children,
  plana = false,
  marcador,
  claseDelRecuadro = "h-60 max-w-[32.5rem]",
}: {
  titulo: string;
  /** El texto del ícono "i". Sin él no se dibuja el ícono. */
  ayuda?: string;
  /** Aclaración bajo el título, siempre visible. Distinta de `ayuda`. */
  nota?: string;
  /** URL de la imagen, o `null` si todavía no hay ninguna. */
  src: string | null;
  /** El tipo real, para el chip: "JPEG", "PNG", "WEBP". */
  mime?: string | null;
  peso?: number | null;
  ocupado?: boolean;
  error?: string | null;
  onArchivo: (archivo: File) => void;
  onQuitar: () => void;
  /** Lo que va DEBAJO de la imagen: en el rol, su descripción de deslindes. */
  children?: React.ReactNode;
  /**
   * Sin tarjeta propia ni título visible: para cuando el llamador ya la mete
   * dentro de una tarjeta numerada con el mismo título. Conserva el estado y
   * los botones; el título sigue nombrando la imagen y el selector de archivo.
   */
  plana?: boolean;
  /** Solo `plana`: el texto del recuadro vacío. Sin él, "{titulo} · JPG, PNG o WEBP". */
  marcador?: string;
  /** Solo `plana`: el tamaño del recuadro. Por defecto el de Suelos (520 × 240 px). */
  claseDelRecuadro?: string;
}) {
  const input = useRef<HTMLInputElement>(null);

  if (plana) {
    return (
      <div className="space-y-3">
        {/* EL RECUADRO DEL DISEÑO: una caja punteada de 520 × 240 px, con el
            ícono y el texto al centro, que ES la zona de soltar. Con imagen
            muestra la imagen y una barra con sus acciones. */}
        <div
          className={cn("relative overflow-hidden rounded-[0.875rem]", claseDelRecuadro)}
          onDragOver={(evento) => evento.preventDefault()}
          onDrop={(evento) => {
            evento.preventDefault();
            const archivo = evento.dataTransfer.files[0];
            if (archivo && !ocupado) onArchivo(archivo);
          }}
        >
          <input
            ref={input}
            type="file"
            accept={EXTENSIONES_ACEPTADAS}
            className="sr-only"
            aria-label={`Adjuntar imagen a ${titulo}`}
            onChange={(evento) => {
              const archivo = evento.target.files?.[0];
              evento.target.value = "";
              if (archivo) onArchivo(archivo);
            }}
          />

          {src ? (
            <>
              <img
                key={src}
                src={src}
                alt={titulo}
                className="bg-muted size-full rounded-[0.875rem] border-[1.5px] object-contain"
              />
              <div className="bg-card/95 absolute inset-x-0 bottom-0 flex flex-wrap items-center gap-2 border-t px-3 py-2">
                {mime ? (
                  <span className="bg-primary text-primary-foreground rounded px-1.5 py-0.5 font-mono text-[0.6875rem] font-bold uppercase">
                    {formatoDeLaImagen(mime)}
                  </span>
                ) : null}
                {peso == null ? null : (
                  <span className="text-ink-hint font-mono text-xs">{formatearPeso(peso)}</span>
                )}
                <a
                  href={src}
                  target="_blank"
                  rel="noreferrer"
                  className="text-ink-muted hover:text-ink text-xs font-semibold hover:underline"
                >
                  Ver en tamaño completo
                </a>
                <div className="ml-auto flex items-center gap-1.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={ocupado}
                    onClick={() => input.current?.click()}
                  >
                    {ocupado ? <Spinner /> : <PaperclipIcon aria-hidden="true" />}
                    Reemplazar imagen
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={ocupado}
                    onClick={onQuitar}
                    className="text-destructive hover:text-destructive"
                  >
                    Eliminar
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <RecuadroPunteado
              ocupado={ocupado}
              marcador={marcador ?? `${titulo} · JPG, PNG o WEBP`}
              onClick={() => input.current?.click()}
            />
          )}
        </div>

        {error ? <p className="text-destructive text-sm">{error}</p> : null}

        {children}
      </div>
    );
  }

  return (
    <div className="bg-card space-y-4 rounded-xl border p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
            <h3 className="text-body text-ink font-bold">{titulo}</h3>
            {ayuda ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* Un botón y no un `<span>`: el tooltip tiene que abrirse con
                      el teclado, y solo un elemento enfocable recibe el foco.
                      `type="button"` porque esta tarjeta vive dentro de un
                      formulario y un botón sin type lo ENVÍA. */}
                  <button
                    type="button"
                    aria-label={`Qué es "${titulo}"`}
                    className="text-ink-hint hover:text-ink focus-visible:ring-ring/50 rounded-full focus-visible:ring-[3px] focus-visible:outline-none"
                  >
                    <InfoIcon className="size-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[18rem]">{ayuda}</TooltipContent>
              </Tooltip>
            ) : null}
        </div>

        <div className="flex items-center gap-3">
          {/* El estado va SIEMPRE visible, con dos rótulos distintos: un
              recuadro vacío se distingue por un chip propio y no por la
              ausencia de contenido — mismo criterio que "Sin definir" en la
              pestaña de documentos. */}
          <Badge variant={src ? "success" : "outline"}>
            {src ? "1 imagen" : "Sin imagen"}
          </Badge>

          <input
            ref={input}
            type="file"
            accept={EXTENSIONES_ACEPTADAS}
            /* `sr-only` y no `display:none` para que el control siga
               existiendo para la tecnología asistiva. */
            className="sr-only"
            aria-label={`Adjuntar imagen a ${titulo}`}
            onChange={(evento) => {
              const archivo = evento.target.files?.[0];
              // Se limpia SIEMPRE, incluso cuando el archivo se acepta: sin
              // esto, volver a elegir el mismo archivo no dispara `change` —el
              // valor no cambió— y reintentar después de un error del servidor
              // sería imposible sin elegir otro archivo primero.
              evento.target.value = "";
              if (archivo) onArchivo(archivo);
            }}
          />

          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={ocupado}
            onClick={() => input.current?.click()}
          >
            {ocupado ? <Spinner /> : <PaperclipIcon aria-hidden="true" />}
            {src ? "Reemplazar imagen" : "Adjuntar imagen"}
          </Button>
        </div>
      </div>

      {nota ? <p className="text-ink-muted text-sm">{nota}</p> : null}

      {src ? (
        <div className="bg-muted/40 flex items-center gap-3 rounded-md p-3">
          {/* `key` con la url: sin él React reutiliza el mismo nodo al
              reemplazar la imagen y, como la URL cambió pero el elemento no,
              algunos navegadores siguen pintando la anterior. */}
          <img
            key={src}
            src={src}
            alt={titulo}
            className="bg-background size-16 shrink-0 rounded-md border object-cover"
          />

          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              {mime ? (
                <span className="bg-primary text-primary-foreground rounded px-1.5 py-0.5 font-mono text-[0.6875rem] font-bold uppercase">
                  {formatoDeLaImagen(mime)}
                </span>
              ) : null}
              {peso == null ? null : (
                <span className="text-ink-hint font-mono text-xs">{formatearPeso(peso)}</span>
              )}
            </div>
            {/* Abre en una pestaña nueva porque la miniatura no alcanza para
                revisar un polígono catastral: hay que poder verla grande. */}
            <a
              href={src}
              target="_blank"
              rel="noreferrer"
              className="text-ink-muted hover:text-ink text-xs font-semibold hover:underline"
            >
              Ver en tamaño completo
            </a>
          </div>

          {/* GHOST Y CON TEXTO ROJO, no `destructive`: el botón sólido rojo se
              reserva para lo irreversible, y esto se deshace volviendo a
              subir la imagen. Es el mismo criterio del módulo de referencias. */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={ocupado}
            onClick={onQuitar}
            className="text-destructive hover:text-destructive shrink-0"
          >
            Eliminar
          </Button>
        </div>
      ) : null}

      {error ? <p className="text-destructive text-sm">{error}</p> : null}

      {children}
    </div>
  );
}

/** "image/jpeg" → "JPEG". El chip dice el formato, no el mime entero. */
function formatoDeLaImagen(mime: string): string {
  return mime.replace(/^image\//, "").toUpperCase();
}
